import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderKanban, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · User-Directive „Meine Projekte-Balken direkt unter den 3
 * Kacheln".
 *
 * Ganz breit statt Kachel — der Concierge nimmt seine Projekte als
 * eigenständigen Block wahr und springt mit einem Klick in die volle
 * Projekte-Seite (`/team/projects`, scoped auf ihn).
 *
 * Datenquelle: `/api/team/concierge-stats.open_projects` — dieselbe Zahl,
 * die auch die Admin-Auslastungstabelle rendert. Kein Doppel-Fetch.
 */
export default function ConciergeProjectsBar() {
  const { user, activeConciergeId } = useAuth();
  const [row, setRow] = useState(null);

  const scopeId = activeConciergeId || (user?.role === "concierge" ? user.id : null);

  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/team/concierge-stats");
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setRow(list.find((r) => r?.concierge?.id === scopeId) || list[0] || null);
      } catch (e) {
        if (!cancelled) setRow(null);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeId]);

  if (!scopeId) return null;
  const isLoading = row === null;
  const projectsCount = row?.open_projects ?? 0;
  const isZero = !isLoading && projectsCount === 0;
  const numberColor = isZero ? BRAND_COLORS.navy : BRAND_COLORS.navyDark;

  return (
    <Link
      to="/team/projects"
      data-testid="concierge-projects-bar"
      data-count={isLoading ? "-" : projectsCount}
      className="mb-8 flex items-center gap-4 md:gap-6 px-5 md:px-6 py-4 md:py-5 bg-white border border-navy/10 hover:border-gold transition-colors group"
    >
      <div
        className="w-11 h-11 md:w-12 md:h-12 shrink-0 flex items-center justify-center"
        style={{
          background: withAlpha(BRAND_COLORS.navy, isZero ? 0.05 : 0.08),
          border: `1px solid ${withAlpha(BRAND_COLORS.navy, isZero ? 0.15 : 0.25)}`,
        }}
      >
        <FolderKanban size={22} strokeWidth={1.5} style={{ color: numberColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-caps text-[10.5px] tracking-[0.16em] text-navy-dark/55 mb-0.5">
          Projekte
        </div>
        <div className="flex items-baseline gap-3 flex-wrap">
          <span
            className="font-display text-3xl md:text-4xl leading-none tabular-nums"
            style={{ color: numberColor }}
            data-testid="concierge-projects-bar-count"
          >
            {isLoading ? "…" : projectsCount}
          </span>
          <span className="text-[14px] text-navy leading-tight">Meine Projekte</span>
          {isZero && (
            <span className="text-[11px] tracking-[0.14em] uppercase text-navy-dark/45">
              · Keine aktiven Projekte
            </span>
          )}
        </div>
      </div>
      <span
        className="hidden md:inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-navy-dark/60 group-hover:text-gold transition-colors shrink-0"
      >
        Zur Projekte-Seite
        <ArrowRight size={14} strokeWidth={1.8} />
      </span>
      <ArrowRight
        size={16}
        strokeWidth={1.8}
        className="md:hidden shrink-0 text-navy-dark/30 group-hover:text-gold transition-colors"
      />
    </Link>
  );
}
